/**
 * Screenwriter shared helpers (web port).
 * Direct rewrite of src/lib/screenwriter.ts — dropped react-native Platform,
 * everything else 1:1 with the original mobile app.
 */

export const COLOR_SLOTS = [
  { key: "author",   color: "#4A90D9", label: "Blue",   hint: "narrator / author" },
  { key: "hero1",    color: "#2ECC8A", label: "Green",  hint: "main character" },
  { key: "hero2",    color: "#7B68EE", label: "Purple", hint: "counterpart" },
  { key: "location", color: "#FF7F6E", label: "Coral",  hint: "location (INT./EXT.)", isLocation: true },
  { key: "extras",   color: "#EBC562", label: "Yellow", hint: "chorus / extras" },
];

export const colorForKey = (key) => {
  if (!key) return null;
  return COLOR_SLOTS.find((s) => s.key === key)?.color || null;
};

export const slotMetaForKey = (key) =>
  COLOR_SLOTS.find((s) => s.key === key) || COLOR_SLOTS[0];

export const nextOrderForColor = (chars, colorKey) => {
  const existing = chars.filter((c) => c.colorKey === colorKey);
  if (existing.length === 0) return 1;
  return Math.max(...existing.map((c) => c.order)) + 1;
};

export const findCharacterByName = (chars, name) => {
  const norm = (name || "").trim().toUpperCase();
  return chars.find((c) => (c.name || "").trim().toUpperCase() === norm);
};

export function characterMapToList(map) {
  const out = [];
  const seen = {};
  for (const [k, v] of Object.entries(map || {})) {
    if (!v || typeof v !== "string") continue;
    const colorKey = ["author", "hero1", "hero2", "location", "extras"].includes(k) ? k : "extras";
    seen[colorKey] = (seen[colorKey] || 0) + 1;
    out.push({ id: `${colorKey}-${seen[colorKey]}`, colorKey, order: seen[colorKey], name: v });
  }
  return out;
}

export function charactersToMap(chars) {
  const map = {};
  for (const slot of COLOR_SLOTS) {
    const first = chars.find((c) => c.colorKey === slot.key && c.order === 1);
    if (first) map[slot.key] = first.name;
  }
  for (const c of chars) map[c.id] = c.name;
  return map;
}

export function blocksToDraftText(blocks) {
  const out = [];
  for (const b of blocks) {
    if (b.type === "location") { out.push(""); out.push(b.text.toUpperCase()); }
    else if (b.type === "dialogue") { out.push(""); out.push((b.role_name || "").toUpperCase() + ":"); out.push(b.text); }
    else if (b.type === "parenthetical") { out.push(`(${b.text})`); }
    else { out.push(""); out.push(b.text); }
  }
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

export function draftTextToBlocks(text, characters) {
  const paragraphs = (text || "").split(/\n{2,}/g).map((p) => p.trim()).filter(Boolean);
  const out = [];
  const locHint = /^(ИНТ\.|ЭКСТ\.|INT\.|EXT\.)/i;
  const nameLine = /^([A-ZА-ЯЁ0-9 .,\-–—]{2,60}):?\s*([\s\S]*)$/;
  for (const p of paragraphs) {
    if (locHint.test(p)) { out.push({ type: "location", text: p }); continue; }
    const firstNl = p.indexOf("\n");
    const head = (firstNl >= 0 ? p.slice(0, firstNl) : p).trim();
    const tail = (firstNl >= 0 ? p.slice(firstNl + 1) : "").trim();
    const m = head.match(nameLine);
    if (m && tail) {
      const potentialName = m[1].replace(/:$/, "").trim();
      const ch = findCharacterByName(characters, potentialName);
      out.push({ type: "dialogue", role_key: ch?.colorKey || ch?.id || "other", role_name: potentialName.toUpperCase(), text: tail });
      continue;
    }
    const single = p.match(/^([A-ZА-ЯЁ0-9 .,\-–—]{2,60}):\s+([\s\S]+)$/);
    if (single) {
      const potentialName = single[1].trim();
      const ch = findCharacterByName(characters, potentialName);
      out.push({ type: "dialogue", role_key: ch?.colorKey || ch?.id || "other", role_name: potentialName.toUpperCase(), text: single[2].trim() });
      continue;
    }
    if (/^\(.+\)$/.test(p)) { out.push({ type: "parenthetical", text: p.slice(1, -1).trim() }); continue; }
    out.push({ type: "action", text: p });
  }
  return out;
}

export function blocksToHtml(blocks, characters, opts) {
  const escape = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
  const nameToColor = (name) => {
    if (!name) return null;
    const ch = findCharacterByName(characters, name);
    if (!ch) return null;
    return colorForKey(ch.colorKey);
  };
  const rowStyle = (color) =>
    opts.colored && color
      ? `border-left: 3px solid ${color}; padding-left: 8px; background: ${color}0F;`
      : "";
  const parts = [];
  for (const b of blocks) {
    if (b.type === "location") {
      const color = opts.colored ? colorForKey("location") : null;
      parts.push(`<div class="loc" style="${rowStyle(color)}">${escape(b.text.toUpperCase())}</div>`);
    } else if (b.type === "dialogue") {
      const color = opts.colored ? nameToColor(b.role_name) : null;
      parts.push(`<div class="dial-block" style="${rowStyle(color)}"><div class="char">${escape((b.role_name || "").toUpperCase())}</div><div class="dial">${escape(b.text)}</div></div>`);
    } else if (b.type === "parenthetical") {
      parts.push(`<div class="par">(${escape(b.text)})</div>`);
    } else {
      parts.push(`<div class="act">${escape(b.text)}</div>`);
    }
  }
  return `<!doctype html>
<html><head>
<meta charset="utf-8"/>
<title>${escape(opts.title)}</title>
<style>
  @page { size: A4; margin: 25mm 25mm 20mm 32mm; }
  html, body { background: #ffffff; color: #000; font-family: 'Courier New', Courier, monospace; font-size: 12pt; line-height: 1.35; }
  h1 { font-size: 14pt; font-weight: 700; text-align: center; margin: 0 0 24pt 0; letter-spacing: 0.5px; }
  .loc  { font-weight: 700; margin: 14pt 0 6pt 0; text-transform: uppercase; }
  .dial-block { margin: 6pt 0; }
  .char { text-align: center; font-weight: 700; text-transform: uppercase; margin-top: 8pt; }
  .dial { margin: 0 6em 0 6em; }
  .par  { text-align: center; font-style: italic; margin: 2pt 4em; color: #333; }
  .act  { margin: 8pt 0; }
</style>
</head><body>
  <h1>${escape(opts.title.toUpperCase())}</h1>
  ${parts.join("\n")}
</body></html>`;
}

export const monoFontFamily = "'Courier New', Courier, monospace";
